import React, { useState, useCallback, useRef } from 'react';
import { LuCode, LuPlay, LuSave, LuCopy, LuCheck, LuX, LuChevronDown } from 'react-icons/lu';

import {
  Box,
  VStack,
  HStack,
  Button,
  Input,
  Text,
  Badge,
  Separator,
  IconButton,
  Popover,
  Portal,
} from '../../index';

interface CodeEditorFilterProps {
  columns: Array<{
    id: string;
    header: string;
    accessorKey?: string;
    accessorFn?: (row: unknown) => unknown;
  }>;
  onApplyFilter: (filterFunction: (row: unknown) => boolean, description: string) => void;
  onClose: () => void;
  initialCode?: string;
  savedFilters?: Array<{
    name: string;
    code: string;
    description: string;
  }>;
  onSaveFilter?: (name: string, code: string, description: string) => void;
}

interface FilterTemplate {
  name: string;
  description: string;
  code: string;
  category: string;
}

const FILTER_TEMPLATES: FilterTemplate[] = [
  {
    name: 'Text Search',
    description: 'Search in multiple text columns',
    category: 'Text',
    code: `// Search in multiple text columns
const searchTerms = "search term".toLowerCase();
return [
  row.name,
  row.description,
  row.category
].some(field =>
  String(field || '').toLowerCase().includes(searchTerms)
);`,
  },
  {
    name: 'Date Range',
    description: 'Filter by date range',
    category: 'Date',
    code: `// Filter by date range
const startDate = new Date('2024-01-01');
const endDate = new Date('2024-12-31');
const dateValue = new Date(row.dateColumn);

return dateValue >= startDate && dateValue <= endDate;`,
  },
  {
    name: 'Numeric Range',
    description: 'Filter by numeric range',
    category: 'Number',
    code: `// Filter by numeric range
const minValue = 0;
const maxValue = 100;
const value = Number(row.numericColumn);

return value >= minValue && value <= maxValue;`,
  },
  {
    name: 'Status Filter',
    description: 'Filter by multiple status values',
    category: 'Status',
    code: `// Filter by multiple status values
const allowedStatuses = ['active', 'pending', 'approved'];
return allowedStatuses.includes(row.status);`,
  },
  {
    name: 'Complex Logic',
    description: 'Combine multiple conditions',
    category: 'Advanced',
    code: `// Complex multi-condition filter
return (
  row.priority === 'high' &&
  row.status !== 'completed' &&
  new Date(row.dueDate) <= new Date() &&
  row.assignedTo === 'current_user'
);`,
  },
];

const generateColumnHints = (columns: CodeEditorFilterProps['columns']) => {
  return `// Available columns:
${columns.map((col) => `// • ${col.id}: ${col.header}`).join('\n')}

// Available variables:
// • row: The current row object
// • index: Current row index (optional)
// • data: Complete dataset (optional)

// Return true to include the row, false to exclude it
`;
};

export const CodeEditorFilter: React.FC<CodeEditorFilterProps> = ({
  columns,
  onApplyFilter,
  onClose,
  initialCode = '',
  savedFilters = [],
  onSaveFilter,
}) => {
  const [code, setCode] = useState(initialCode || generateColumnHints(columns));
  const [filterName, setFilterName] = useState('');
  const [description, setDescription] = useState('');
  const [showTemplates, setShowTemplates] = useState(false);
  const [showSaved, setShowSaved] = useState(false);
  const [_selectedTemplate, setSelectedTemplate] = useState<FilterTemplate | null>(null);
  const [testResult, setTestResult] = useState<{ passed: boolean; message: string } | null>(null);
  const [isTesting, setIsTesting] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleTemplateSelect = useCallback(
    (template: FilterTemplate) => {
      const newCode = generateColumnHints(columns) + '\n\n' + template.code;
      setCode(newCode);
      setSelectedTemplate(template);
      setShowTemplates(false);
      setDescription(template.description);
    },
    [columns]
  );

  const handleSavedFilterSelect = useCallback((filter: (typeof savedFilters)[0]) => {
    setCode(filter.code);
    setDescription(filter.description);
    setShowSaved(false);
  }, []);

  const handleTestFilter = useCallback(async () => {
    setIsTesting(true);
    setTestResult(null);

    try {
      // Create a test function from the code
      const testFunction = new Function(
        'row',
        'index',
        'data',
        `
        try {
          ${code}
        } catch (error) {
          return { error: error.message };
        }
      `
      );

      // Test with first few rows if available (this would be passed actual data in real usage)
      const testRow = {
        test: 'value',
        number: 42,
        date: new Date().toISOString(),
        status: 'active',
      };

      const result = testFunction(testRow, 0, [testRow]);

      if (typeof result === 'boolean') {
        setTestResult({
          passed: true,
          message: 'Filter syntax is valid ✓',
        });
      } else if (result && typeof result === 'object' && 'error' in result) {
        setTestResult({
          passed: false,
          message: `Error: ${result.error}`,
        });
      } else {
        setTestResult({
          passed: true,
          message: 'Filter function compiled successfully ✓',
        });
      }
    } catch (error) {
      setTestResult({
        passed: false,
        message: `Syntax error: ${error instanceof Error ? error.message : 'Unknown error'}`,
      });
    } finally {
      setIsTesting(false);
    }
  }, [code]);

  const handleApplyFilter = useCallback(() => {
    try {
      // Create the actual filter function
      const filterFunction = new Function(
        'row',
        'index',
        'data',
        `
        try {
          const result = (function() {
            ${code}
          })();
          return Boolean(result);
        } catch (error) {
          // Silent error handling for production
          return false;
        }
      `
      );

      onApplyFilter(filterFunction as (row: unknown) => boolean, description || 'Custom filter');
      onClose();
    } catch (error) {
      setTestResult({
        passed: false,
        message: `Failed to create filter: ${error instanceof Error ? error.message : 'Unknown error'}`,
      });
    }
  }, [code, description, onApplyFilter, onClose]);

  const handleSaveFilter = useCallback(() => {
    if (!filterName.trim() || !onSaveFilter) return;

    onSaveFilter(filterName.trim(), code, description);
    setFilterName('');
    setShowSaved(false);
  }, [filterName, code, description, onSaveFilter]);

  const handleCopyCode = useCallback(() => {
    navigator.clipboard.writeText(code);
  }, [code]);

  const textareaStyle = {
    width: '100%',
    minHeight: '200px',
    backgroundColor: 'transparent',
    border: 'none',
    outline: 'none',
    color: '#E5E7EB',
    fontFamily: 'Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
    fontSize: '13px',
    lineHeight: '1.5',
    resize: 'vertical' as const,
  };

  return (
    <Popover.Root open={true} onOpenChange={onClose}>
      <Portal>
        <Popover.Positioner>
          <Popover.Content width="600px">
            <VStack gap={4} padding={6} align="stretch">
              {/* Header */}
              <HStack justifyContent="space-between" alignItems="center">
                <HStack gap={2}>
                  <LuCode size={20} color="#3B82F6" />
                  <Text fontSize="lg" fontWeight="semibold" color="#1F2937">
                    Code Editor Filter
                  </Text>
                  <Badge colorPalette="blue">Advanced</Badge>
                </HStack>
                <IconButton
                  aria-label="Close"
                  icon={<LuX />}
                  size="sm"
                  variant="ghost"
                  onClick={onClose}
                />
              </HStack>

              {/* Description Input */}
              <VStack gap={2} align="stretch">
                <Text fontSize="sm" fontWeight="medium" color="#374151">
                  Filter Description
                </Text>
                <Input
                  placeholder="Describe what this filter does..."
                  value={description}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setDescription(e.target.value)
                  }
                />
              </VStack>

              {/* Template and Saved Filters */}
              <HStack gap={2}>
                <Popover.Root open={showTemplates} onOpenChange={setShowTemplates}>
                  <Popover.Trigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      leftIcon={<LuCode />}
                      rightIcon={<LuChevronDown />}
                    >
                      Templates
                    </Button>
                  </Popover.Trigger>
                  <Popover.Positioner>
                    <Popover.Content width="400px">
                      <VStack gap={2} padding={4} align="stretch">
                        <Text fontSize="sm" fontWeight="medium" color="#374151">
                          Filter Templates
                        </Text>
                        <Separator />
                        {FILTER_TEMPLATES.map((template) => (
                          <Box
                            key={template.name}
                            padding={2}
                            borderRadius="md"
                            cursor="pointer"
                            _hover={{ backgroundColor: '#F9FAFB' }}
                            onClick={() => handleTemplateSelect(template)}
                          >
                            <VStack gap={1} align="flex-start">
                              <HStack justifyContent="space-between" width="full">
                                <Text fontSize="sm" fontWeight="medium" color="#1F2937">
                                  {template.name}
                                </Text>
                                <Badge colorPalette="gray" size="sm">
                                  {template.category}
                                </Badge>
                              </HStack>
                              <Text fontSize="xs" color="#6B7280">
                                {template.description}
                              </Text>
                            </VStack>
                          </Box>
                        ))}
                      </VStack>
                    </Popover.Content>
                  </Popover.Positioner>
                </Popover.Root>

                {savedFilters.length > 0 && (
                  <Popover.Root open={showSaved} onOpenChange={setShowSaved}>
                    <Popover.Trigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        leftIcon={<LuSave />}
                        rightIcon={<LuChevronDown />}
                      >
                        Saved Filters
                      </Button>
                    </Popover.Trigger>
                    <Popover.Positioner>
                      <Popover.Content width="400px">
                        <VStack gap={2} padding={4} align="stretch">
                          <Text fontSize="sm" fontWeight="medium" color="#374151">
                            Saved Filters
                          </Text>
                          <Separator />
                          {savedFilters.map((filter, index) => (
                            <Box
                              key={index}
                              padding={2}
                              borderRadius="md"
                              cursor="pointer"
                              _hover={{ backgroundColor: '#F9FAFB' }}
                              onClick={() => handleSavedFilterSelect(filter)}
                            >
                              <VStack gap={1} align="flex-start">
                                <Text fontSize="sm" fontWeight="medium" color="#1F2937">
                                  {filter.name}
                                </Text>
                                <Text fontSize="xs" color="#6B7280">
                                  {filter.description}
                                </Text>
                              </VStack>
                            </Box>
                          ))}
                        </VStack>
                      </Popover.Content>
                    </Popover.Positioner>
                  </Popover.Root>
                )}
              </HStack>

              {/* Code Editor */}
              <VStack gap={2} align="stretch">
                <HStack justifyContent="space-between" alignItems="center">
                  <Text fontSize="sm" fontWeight="medium" color="#374151">
                    Filter Code
                  </Text>
                  <HStack gap={2}>
                    <IconButton
                      aria-label="Copy code"
                      icon={<LuCopy />}
                      size="xs"
                      variant="ghost"
                      onClick={handleCopyCode}
                    />
                    <Button
                      size="xs"
                      variant="outline"
                      leftIcon={<LuPlay />}
                      onClick={handleTestFilter}
                      isLoading={isTesting}
                    >
                      Test
                    </Button>
                  </HStack>
                </HStack>

                <Box
                  backgroundColor="#1F2937"
                  borderRadius="md"
                  padding={3}
                  border="1px solid"
                  borderColor="#374151"
                >
                  <textarea
                    ref={textareaRef}
                    value={code}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                      setCode(e.target.value)
                    }
                    style={textareaStyle}
                    placeholder="Write your filter function here..."
                    spellCheck={false}
                  />
                </Box>

                {/* Test Result */}
                {testResult && (
                  <HStack
                    gap={2}
                    padding={2}
                    borderRadius="md"
                    backgroundColor={testResult.passed ? '#D1FAE5' : '#FEE2E2'}
                  >
                    {testResult.passed ? (
                      <LuCheck size={16} color="#059669" />
                    ) : (
                      <LuX size={16} color="#DC2626" />
                    )}
                    <Text fontSize="sm" color={testResult.passed ? '#065F46' : '#991B1B'}>
                      {testResult.message}
                    </Text>
                  </HStack>
                )}
              </VStack>

              {/* Save Filter */}
              {onSaveFilter && (
                <VStack gap={2} align="stretch">
                  <Separator />
                  <HStack gap={2}>
                    <Input
                      placeholder="Filter name..."
                      value={filterName}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setFilterName(e.target.value)
                      }
                      flex="1"
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      leftIcon={<LuSave />}
                      onClick={handleSaveFilter}
                      disabled={!filterName.trim()}
                    >
                      Save
                    </Button>
                  </HStack>
                </VStack>
              )}

              {/* Actions */}
              <HStack gap={2} justifyContent="flex-end">
                <Button variant="outline" onClick={onClose}>
                  Cancel
                </Button>
                <Button leftIcon={<LuPlay />} onClick={handleApplyFilter} disabled={!code.trim()}>
                  Apply Filter
                </Button>
              </HStack>
            </VStack>
          </Popover.Content>
        </Popover.Positioner>
      </Portal>
    </Popover.Root>
  );
};
